import { useParams, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { SERVICES } from "@/data/siteData";

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const service = SERVICES.find(s => s.id === serviceId);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] pt-20">
        <p className="text-[#A3A3A3]">Loading...</p>
      </div>
    );
  }

  return (
    <main data-testid="service-detail-page" className="pt-20">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${service.image})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
        <div className="relative z-10 h-full flex items-end pb-12">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
            <Link
              to="/services"
              data-testid="back-to-services"
              className="inline-flex items-center gap-2 text-[#D4AF37] text-sm mb-4 hover:text-[#F0C74A] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> All Services
            </Link>
            <h1 className="font-serif text-4xl sm:text-5xl text-white">{service.title}</h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <p className="font-sans text-base text-[#A3A3A3] leading-relaxed mb-8">
                {service.description}
              </p>
              <h3 className="font-serif text-2xl text-white mb-6">Key Features</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {service.features?.map((feat, i) => (
                  <div
                    key={i}
                    data-testid={`feature-${i}`}
                    className="flex items-center gap-3 bg-[#121212] border border-white/5 p-4"
                  >
                    <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-white text-sm font-sans">{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar CTA */}
            <div>
              <div className="bg-[#121212] border border-[#D4AF37]/20 p-8 sticky top-28">
                <h3 className="font-serif text-xl text-white mb-3">Interested in {service.title}?</h3>
                <p className="font-sans text-sm text-[#A3A3A3] mb-6">
                  Get a free, no-obligation quote from our experts today. We'll visit your site, take measurements, and provide a detailed proposal.
                </p>
                <Link
                  to="/contact"
                  data-testid="detail-cta-quote"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-8 py-4 text-sm uppercase tracking-wider hover:bg-[#F0C74A] transition-colors"
                >
                  Get a Free Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="tel:+61400000000"
                  data-testid="detail-call"
                  className="w-full mt-3 inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-white font-semibold px-8 py-4 text-sm uppercase tracking-wider hover:bg-[#D4AF37]/10 transition-colors"
                >
                  Call Us Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
